import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Save, AlertCircle, CheckCircle, Radio } from 'lucide-react';
import ReadOnlyFieldset, { ReadOnlyNote } from './ui/ReadOnlyFieldset';
import Button from './ui/Button';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import { resolveFunctionsTarget } from '../utils/environmentConfig';
import { trackException, getCurrentTraceId } from '../services/appInsightsService';

/**
 * RealtimeProviderPolicySettings — Admin Tools panel for the three persisted realtime provider
 * settings: direct Simultaneous translation, Precise speech recognition, and dictation — plus each
 * provider's readiness on the backend and its concurrent-session limit.
 * Backed by GET/PUT /platform/realtime-provider-policy.
 *
 * Sprint 2 of plans/Simultaneous-Provider-Policy-Separation-Plan.md, extended by Sprint 3 of
 * plans/OpenAI-Gemini-First-Class-Realtime-Plan.md (decisions 7, 7-1, F6: this panel is the only
 * switch). Three rules drive the UI, and each one is a way this panel could look correct while
 * being wrong:
 *
 *   1. A failed LOAD renders an error and NO selector at all. Showing `soniox_only` in a
 *      selector because the GET failed presents a policy that was never read.
 *   2. A failed SAVE leaves the displayed selection on the last PERSISTED values, never on the
 *      attempted ones — otherwise the operator reads "OpenAI" off a screen where Soniox is live.
 *   3. An UNRESOLVED backend target disables all selectors and Save. A panel that silently
 *      posted to the hard-coded staging default would let an operator believe they had changed
 *      production.
 *
 * English copy only (user-authorized localization exception, 2026-09-03) — AdminPortal carries
 * no i18n framework.
 */

/**
 * The canonical provider modes. Values are the WIRE literals shared with
 * RealtimeProviderModes.cs; the labels are operator-facing only.
 */
const MODE_OPTIONS = [
    { value: 'soniox_only', label: 'Soniox only' },
    { value: 'openai_only', label: 'OpenAI only' },
    { value: 'gemini_only', label: 'Gemini only' },
    { value: 'all', label: 'All providers (server chooses)' },
];

/**
 * Read-aloud TTS provider modes (#3502, decision A). WIRE literals of ReadAloudTtsModes.cs.
 * The OpenAI fallback is used only when Soniox refuses a read-aloud key.
 */
const READ_ALOUD_OPTIONS = [
    { value: 'soniox_only', label: 'Soniox only' },
    { value: 'soniox_then_openai', label: 'Soniox, then OpenAI when Soniox is full' },
];

/** Keys are the WIRE names of RealtimeProviderConcurrencyLimits / providerReadiness. */
const PROVIDERS = [
    { key: 'soniox', label: 'Soniox' },
    { key: 'openAi', label: 'OpenAI' },
    { key: 'gemini', label: 'Gemini' },
];

/** Mirrors RealtimeProviderConcurrencyLimits.MinimumLimit / MaximumLimit, which the server enforces. */
const MIN_LIMIT = 1;
const MAX_LIMIT = 10000;

const POLICY_PATH = '/platform/realtime-provider-policy';

/**
 * Never sends an empty X-Trace-Id. An admin write to platform configuration that arrives
 * uncorrelated cannot be tied back to its server-side audit event, so a local correlation id
 * is minted rather than sending a blank header.
 */
const traceIdHeader = () => getCurrentTraceId() || `adminportal-${Date.now().toString(36)}`;

const metaStyle = {
    fontSize: '12px',
    color: 'var(--text-secondary, #6b7280)',
    margin: '0 0 16px 0',
};

// `--error`/`--success` are the same static hex in both themes, so they cannot serve as TEXT
// on a background tinted from --bg-card: whichever contrast they satisfy in light mode, they
// fail in dark mode, or vice versa (a 12%-into-white tint and a 12%-into-#1e293b tint sit at
// opposite ends of the luminance range). --text-primary already flips per theme to contrast
// against --bg-card, and a 12% tint barely moves off it, so it stays readable in both. The
// icon alone (see noticeIconStyle below) carries the error/success colour instead.
const noticeStyle = (isError) => ({
    marginTop: '12px',
    padding: '8px 12px',
    borderRadius: '6px',
    fontSize: '13px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: isError
        ? 'color-mix(in srgb, var(--error, #dc2626) 12%, var(--bg-card, #fff))'
        : 'color-mix(in srgb, var(--success, #059669) 12%, var(--bg-card, #fff))',
    color: 'var(--text-primary, #111827)',
});

/** Semantic colour for the notice's icon only -- text uses noticeStyle's --text-primary instead. */
const noticeIconStyle = (isError) => ({
    color: isError ? 'var(--error, #dc2626)' : 'var(--success, #059669)',
    flexShrink: 0,
});

const canonicalMode = (mode) => (mode === 'both' ? 'all' : mode);

/**
 * Extracts the editable selection from a persisted policy payload. Limits are absent only when the
 * backend predates them; the panel then neither shows nor sends them.
 */
const draftOf = (policy) => (policy
    ? {
        simultaneousTranslationMode: canonicalMode(policy.simultaneousTranslationMode),
        translationMode: canonicalMode(policy.translationMode),
        transcriptionMode: canonicalMode(policy.transcriptionMode),
        // Absent only when the backend predates the read-aloud axis; then it is neither shown nor sent.
        readAloudTtsMode: policy.readAloudTtsMode,
        providerConcurrencyLimits: policy.providerConcurrencyLimits
            ? { ...policy.providerConcurrencyLimits }
            : undefined,
    }
    : null);

/** Returns the first out-of-range limit's message, or null when every limit is a whole number in range. */
const limitError = (limits) => {
    const invalid = PROVIDERS.find(({ key }) => {
        const value = Number(limits[key]);
        return !Number.isInteger(value) || value < MIN_LIMIT || value > MAX_LIMIT;
    });
    return invalid
        ? `${invalid.label} concurrency limit must be a whole number from ${MIN_LIMIT} to ${MAX_LIMIT}.`
        : null;
};

const RealtimeProviderPolicySettings = ({ readOnly = false }) => {
    const [persisted, setPersisted] = useState(null);
    const [draft, setDraft] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [saveStatus, setSaveStatus] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    // Resolved once: the backend target cannot change while the panel is mounted, and a fresh
    // object every render would re-arm the load effect forever.
    const target = useMemo(() => resolveFunctionsTarget(
        process.env.REACT_APP_FUNCTIONS_URL,
        process.env.REACT_APP_WEBSOCKET_URL,
        window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1',
    ), []);

    const endpoint = `${target.baseUrl}${POLICY_PATH}`;

    const load = useCallback(async () => {
        // An unresolved target is the hard-coded staging default, not a deliberate backend. Do not
        // send an authenticated request to it at all — that would leak this console's JWT into the
        // wrong environment and read a policy the operator is not looking at.
        if (!target.resolved) return;

        setIsLoading(true);
        setLoadError(null);
        try {
            const response = await fetchWithAuth(endpoint, {
                method: 'GET',
                headers: { 'X-Trace-Id': traceIdHeader() },
            });
            if (!response.ok) {
                throw new Error(`Failed to load the realtime provider policy (HTTP ${response.status}).`);
            }
            const policy = await response.json();
            // A payload without all three modes or a resolved environment flag is not a policy —
            // rendering from it would present an empty default as if it were persisted.
            if (typeof policy?.simultaneousTranslationMode !== 'string'
                || typeof policy?.translationMode !== 'string'
                || typeof policy?.transcriptionMode !== 'string'
                || typeof policy?.openAiEnabled !== 'boolean') {
                throw new Error('The realtime provider policy response was missing required provider data.');
            }
            setPersisted(policy);
            setDraft(draftOf(policy));
        } catch (err) {
            trackException(err, { errorType: 'RealtimeProviderPolicy_LoadFailed' });
            setPersisted(null);
            setDraft(null);
            setLoadError(err.message || 'Failed to load the realtime provider policy.');
        } finally {
            setIsLoading(false);
        }
    }, [endpoint, target.resolved]);

    useEffect(() => {
        load();
    }, [load]);

    const handleSave = async () => {
        if (readOnly) return;
        setSaveStatus(null);
        const limits = draft.providerConcurrencyLimits;
        const invalidLimit = limits && limitError(limits);
        if (invalidLimit) {
            setSaveStatus({ type: 'error', message: invalidLimit });
            return;
        }

        setIsSaving(true);
        try {
            const response = await fetchWithAuth(endpoint, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Trace-Id': traceIdHeader(),
                },
                // Atomic over all three axes, the provider limits, and the loaded ETag, so a concurrent
                // edit is rejected by the server rather than half-applied here.
                body: JSON.stringify({
                    simultaneousTranslationMode: draft.simultaneousTranslationMode,
                    translationMode: draft.translationMode,
                    transcriptionMode: draft.transcriptionMode,
                    readAloudTtsMode: draft.readAloudTtsMode,
                    providerConcurrencyLimits: limits
                        ? Object.fromEntries(PROVIDERS.map(({ key }) => [key, Number(limits[key])]))
                        : undefined,
                    etag: persisted.etag,
                }),
            });

            if (response.status === 412) {
                setDraft(draftOf(persisted));
                setSaveStatus({
                    type: 'error',
                    message: 'Someone else changed this policy. Reload the panel before saving again.',
                });
                // The panel carries no reload control and `load` only runs on mount, so without this
                // the next click reuses the same stale ETag and is a guaranteed second 412.
                await load();
                return;
            }
            if (!response.ok) {
                throw new Error(`Failed to save the realtime provider policy (HTTP ${response.status}).`);
            }

            const saved = await response.json();
            setPersisted(saved);
            setDraft(draftOf(saved));
            setSaveStatus({ type: 'success', message: 'Saved.' });
        } catch (err) {
            trackException(err, { errorType: 'RealtimeProviderPolicy_SaveFailed' });
            // The displayed selection returns to what is actually persisted.
            setDraft(draftOf(persisted));
            setSaveStatus({ type: 'error', message: err.message || 'Failed to save the realtime provider policy.' });
        } finally {
            setIsSaving(false);
        }
    };

    const selectFor = (id, labelText, field, options = MODE_OPTIONS) => (
        <div className="provider-policy-row">
            <label htmlFor={id} style={{ fontSize: '14px' }}>{labelText}</label>
            <select
                id={id}
                value={draft ? draft[field] : ''}
                disabled={!target.resolved || isSaving}
                onChange={(e) => setDraft((d) => ({ ...d, [field]: e.target.value }))}
            >
                {/* No policy was read, so no policy value is offered as the selection. */}
                {!draft && <option value="">Not available</option>}
                {options.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                ))}
            </select>
        </div>
    );

    const limitInputFor = ({ key, label }) => {
        const id = `realtime-${key.toLowerCase()}-concurrency-limit`;
        return (
            <div className="provider-policy-row" key={key}>
                <label htmlFor={id} style={{ fontSize: '14px' }}>{`${label} concurrency limit`}</label>
                <input
                    id={id}
                    type="number"
                    min={MIN_LIMIT}
                    max={MAX_LIMIT}
                    step={1}
                    value={draft.providerConcurrencyLimits[key]}
                    disabled={!target.resolved || isSaving}
                    onChange={(e) => {
                        const { value } = e.target;
                        setDraft((d) => ({
                            ...d,
                            providerConcurrencyLimits: { ...d.providerConcurrencyLimits, [key]: value },
                        }));
                    }}
                />
            </div>
        );
    };

    const readinessText = (readiness) => (readiness?.ready
        ? 'Ready'
        : `Not ready (${readiness?.reason || 'unknown'})`);

    return (
        <section className="provider-policy-card" data-testid="realtime-provider-policy-settings">
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                <Radio size={20} />
                <h3 style={{ margin: 0 }}>Realtime provider policy</h3>
            </div>
            <p style={metaStyle}>
                Which speech provider the platform may use, per axis. Backend target: {target.baseUrl}
                {!target.resolved && ' — UNRESOLVED. No backend URL is configured, so saving is disabled.'}
            </p>

            {persisted && (
                <>
                    <p style={metaStyle} data-testid="realtime-provider-policy-version">
                        Version {persisted.realtimeProviderPolicyVersion}
                        {persisted.lastModifiedBy ? ` · last changed by ${persisted.lastModifiedBy}` : ''}
                        {persisted.lastModifiedAtUtc ? ` at ${new Date(persisted.lastModifiedAtUtc).toLocaleString()}` : ''}
                    </p>
                    {persisted.providerReadiness && (
                        <ul style={{ ...metaStyle, paddingLeft: '16px' }} data-testid="realtime-provider-readiness">
                            {PROVIDERS.map(({ key, label }) => (
                                <li key={key}>{`${label}: ${readinessText(persisted.providerReadiness[key])}`}</li>
                            ))}
                        </ul>
                    )}
                </>
            )}

            <ReadOnlyNote readOnly={readOnly} />

            {isLoading && !draft && <p>Loading policy…</p>}

            {loadError && (
                <div style={noticeStyle(true)} role="alert">
                    <AlertCircle size={18} style={noticeIconStyle(true)} />
                    {loadError}
                </div>
            )}

            {/*
              * Rendered with a real selection once the policy is loaded, and — when the backend
              * target is unresolved — as three DISABLED, empty selectors so the operator can see the
              * settings this console governs without being shown a policy nobody read.
              * A failed LOAD is the third case and renders no selector at all.
              */}
            <ReadOnlyFieldset readOnly={readOnly}>
            {(draft || !target.resolved) && (
                <>
                    {selectFor(
                        'realtime-simultaneous-translation-provider-mode',
                        'Simultaneous translation',
                        'simultaneousTranslationMode',
                    )}
                    {selectFor(
                        'realtime-translation-provider-mode',
                        'Precise speech recognition',
                        'translationMode',
                    )}
                    <p style={metaStyle}>
                        Precise selects the speech recognition provider only. Its translation model still comes from the existing quality settings.
                    </p>
                    {selectFor('realtime-dictation-provider-mode', 'Real-time dictation', 'transcriptionMode')}
                </>
            )}

            {typeof draft?.readAloudTtsMode === 'string' && selectFor(
                'read-aloud-tts-provider-mode',
                'Read-aloud (Listen)',
                'readAloudTtsMode',
                READ_ALOUD_OPTIONS,
            )}

            {draft?.providerConcurrencyLimits && PROVIDERS.map(limitInputFor)}

            <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
                <Button
                    onClick={handleSave}
                    disabled={readOnly || !target.resolved || !draft || isSaving}
                    isLoading={isSaving}
                    icon={<Save size={16} />}
                    variant="primary"
                    size="sm"
                >
                    Save
                </Button>
            </div>
            </ReadOnlyFieldset>

            {saveStatus && (
                <div
                    style={noticeStyle(saveStatus.type === 'error')}
                    role={saveStatus.type === 'error' ? 'alert' : 'status'}
                >
                    {saveStatus.type === 'success'
                        ? <CheckCircle size={16} style={noticeIconStyle(false)} />
                        : <AlertCircle size={16} style={noticeIconStyle(true)} />}
                    {saveStatus.message}
                </div>
            )}
        </section>
    );
};

export default RealtimeProviderPolicySettings;
